import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { activeEbookEditorResult } from '../services/ebook-editor-lifecycle';
import type {
  EbookDocument,
  EbookQuestion,
  EbookMove,
  EbookRecognitionMode,
} from '@wx-weiqi/contracts';
import {
  EbookOptimizationControl,
  ebookRecognitionLabels,
  ebookOptimizationSummary,
} from './EbookOptimizationControl';
import { EbookClient } from '../services/ebooks';
import {
  anchorQuestion,
  resizeQuestion,
  pathFor,
  transformQuestion,
  sourceShowsFullBoard,
  outsideModelProblem,
  chooseOutsideModel,
} from '../services/ebook-editing';
import { BoardSurface } from './BoardSurface';
import { EbookEditorTrial } from './EbookEditorTrial';
import { EbookEditorBoardFrame } from './EbookEditorBoardFrame';
import { EbookEditorSettingsDialog } from './EbookEditorSettingsDialog';
import {
  deleteEbookAnswerBranch,
  restoreEbookAnswerBranch,
  navigateEbookAnswer,
  ebookEditorInputAllowed,
  ebookWheelNavigation,
  initialEbookWheelState,
  ebookAnswerPositionKey,
  type DeletedEbookAnswerBranch,
} from '../services/ebook-editor-commands';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import './ebook-editor-workspace.css';
import { EbookVariationTree } from './EbookVariationTree';
import { EbookBranchTrails } from './EbookBranchTrails';
import { EbookChapterSelect } from './EbookChapterSelect';
import { ClassroomZoomControl } from './ClassroomZoomControl';
import { boardViewportForCorner, type HomeworkBoardCorner } from '../domain/homework-board-focus';
import { normalizeEbookChapter } from '../services/ebook-chapters';
import { secureRandomUuid } from '../services/random-id';
import {
  ebookMoveLabels,
  branchRecord,
  branchIsSaved,
  savedBranchEnds,
  savedNextMoves,
} from '../services/ebook-variations';
export function EbookSource({
  client,
  document,
  pageIndex,
  question,
  onRegion,
  onOptimize,
  busy = false,
  recognitionAvailable = true,
}: {
  client: EbookClient;
  document: EbookDocument;
  pageIndex: number;
  question: EbookQuestion | null;
  onRegion: (region: { x: number; y: number; width: number; height: number }) => void;
  onOptimize?: (mode: EbookRecognitionMode) => Promise<void>;
  busy?: boolean;
  recognitionAvailable?: boolean;
}) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [select, setSelect] = useState(false);
  const [overlay, setOverlay] = useState(true);
  const [originalPage, setOriginalPage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const enhancementLock = useRef(false);
  const [selection, setSelection] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const group = useRef<SVGGElement>(null);
  const [metadata, setMetadata] = useState<{ key: string; width: number; height: number } | null>(
    null,
  );
  const page = document.pages.find((p) => p.index === pageIndex);
  const result = page?.result;
  const pageKey = `${document.id}:${pageIndex}`;
  const previewSource = result?.enhancement && originalPage !== pageKey ? 'enhanced' : 'original';
  const processing = page?.status === 'QUEUED' || page?.status === 'RUNNING';
  const enhancing = processing;
  const enhance = async (mode: EbookRecognitionMode) => {
    if (!onOptimize || enhancementLock.current || busy || processing || !recognitionAvailable)
      return;
    enhancementLock.current = true;
    setSubmitting(true);
    setSelect(false);
    setSelection(null);
    try {
      await onOptimize(mode);
      setOriginalPage(null);
    } catch (e) {
      setError(userErrorMessage(e, '识别任务提交失败，请重试'));
    } finally {
      enhancementLock.current = false;
      setSubmitting(false);
    }
  };
  const geometry = result ?? (metadata?.key === `${document.id}:${pageIndex}` ? metadata : null);
  const width = geometry?.width ?? 1,
    height = geometry?.height ?? 1;
  useEffect(() => {
    let alive = true;
    let object = '';
    setError('');
    setUrl('');
    setSelection(null);
    start.current = null;
    void Promise.all([
      client.image(document.id, pageIndex, previewSource),
      result ? Promise.resolve(result) : client.pageGeometry(document.id, pageIndex),
    ])
      .then(([blob, dimensions]) => {
        object = URL.createObjectURL(blob);
        if (alive) {
          setMetadata({ ...dimensions, key: `${document.id}:${pageIndex}` });
          setUrl(object);
        } else URL.revokeObjectURL(object);
      })
      .catch((e) => {
        if (alive) setError(userErrorMessage(e, '预览失败'));
      });
    return () => {
      alive = false;
      if (object) URL.revokeObjectURL(object);
    };
  }, [
    client,
    document.id,
    pageIndex,
    previewSource,
    result?.optimization?.appliedMode,
    result?.enhancement,
  ]);
  const map = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!geometry || !url) return null;
    const m = group.current?.getScreenCTM();
    if (!m) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: Math.max(0, Math.min(width, p.x)), y: Math.max(0, Math.min(height, p.y)) };
  };
  return (
    <>
      <div className="ebook-row">
        <h3>
          {previewSource === 'enhanced' ? '优化预览' : '原 PDF'} · 第 {pageIndex + 1} 页
        </h3>
      </div>
      <div className="ebook-actions">
        <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>放大</button>
        <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>缩小</button>
        <button onClick={() => setRotation((r) => (r + 90) % 360)}>旋转预览</button>
        <button aria-pressed={overlay} onClick={() => setOverlay(!overlay)}>
          格点叠加
        </button>
        <button
          disabled={!geometry || !url || busy || processing || submitting}
          aria-pressed={select}
          onClick={() => setSelect(!select)}
        >
          补框 / 重识别
        </button>
        {onOptimize && (
          <EbookOptimizationControl
            disabled={
              !geometry || !url || busy || processing || submitting || !recognitionAvailable
            }
            pending={!!enhancing || submitting}
            label={
              submitting
                ? '正在提交…'
                : enhancing
                  ? document.cancelled
                    ? '优化已暂停'
                    : page?.status === 'RUNNING'
                      ? '正在识别…'
                      : '识别排队中…'
                  : '优化识别'
            }
            onChoose={(mode) => void enhance(mode)}
          />
        )}
        {result?.enhancement && (
          <button onClick={() => setOriginalPage(previewSource === 'enhanced' ? pageKey : null)}>
            {previewSource === 'enhanced' ? '查看原图' : '查看优化图'}
          </button>
        )}
      </div>
      {onOptimize && (
        <p className="ebook-muted" role="status">
          {enhancing
            ? document.cancelled
              ? '识别任务已暂停，勾选本页并点击“识别所选页”可恢复。'
              : `正在按“${ebookRecognitionLabels[page?.recognitionMode ?? 'AUTO']}”处理本页，离开页面不影响处理。`
            : result?.optimization
              ? ebookOptimizationSummary(result)
              : result?.enhancement
                ? '本页为旧版增强结果，可重新优化；切换查看只改变预览，红点仍需校对。'
                : '自动比较原图与优化结果；也可从右侧菜单选择淡印增强、透印抑制或按原图重新识别。原 PDF 和已校对题目保留。'}
        </p>
      )}
      {!url && !error && (
        <p role="status" className="ebook-muted">
          正在加载第 {pageIndex + 1} 页预览…
        </p>
      )}
      {error && <p className="ebook-error">{error}</p>}
      <div className="ebook-source">
        <svg
          aria-label={previewSource === 'enhanced' ? 'PDF 优化页面选框预览' : 'PDF 原页选框预览'}
          viewBox={`0 0 ${rotation % 180 ? height : width} ${rotation % 180 ? width : height}`}
          style={{ width: `${zoom * 100}%` }}
          onPointerDown={(e) => {
            if (!select || busy || processing || submitting) return;
            start.current = map(e);
            if (start.current) e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!start.current) return;
            const p = map(e);
            if (p)
              setSelection({
                x: Math.min(start.current.x, p.x),
                y: Math.min(start.current.y, p.y),
                width: Math.abs(p.x - start.current.x),
                height: Math.abs(p.y - start.current.y),
              });
          }}
          onPointerUp={() => {
            start.current = null;
          }}
          onPointerCancel={() => {
            start.current = null;
            setSelection(null);
          }}
        >
          <g
            ref={group}
            transform={
              rotation === 90
                ? `translate(${height},0) rotate(90)`
                : rotation === 180
                  ? `translate(${width},${height}) rotate(180)`
                  : rotation === 270
                    ? `translate(0,${width}) rotate(270)`
                    : undefined
            }
          >
            {url && (
              <image
                href={url}
                x="0"
                y="0"
                width={width}
                height={height}
                preserveAspectRatio="none"
              />
            )}
            {overlay &&
              page?.status === 'DONE' &&
              result?.candidates.map((c) => (
                <g key={c.id}>
                  <rect {...c.region} fill="none" stroke="#b55526" strokeWidth="1.2" />
                  <g transform={`translate(${c.grid.x},${c.grid.y}) rotate(${c.grid.angle})`}>
                    {c.evidence.map((p) => (
                      <circle
                        key={`${p.x}:${p.y}`}
                        cx={p.x * c.grid.dx}
                        cy={p.y * c.grid.dy}
                        r={p.risk ? 4 : 1.5}
                        fill={p.risk ? '#d33232' : '#168682'}
                        opacity=".7"
                      />
                    ))}
                  </g>
                </g>
              ))}
            {question?.source?.pageIndex === pageIndex && (
              <rect {...question.source.region} stroke="#217576" strokeWidth="2" fill="none" />
            )}
            {selection && <rect {...selection} fill="#1a847c33" stroke="#16756e" strokeWidth="2" />}
          </g>
        </svg>
      </div>
      {select && selection && (
        <button
          className="primary"
          disabled={
            selection.width < 30 || selection.height < 30 || busy || processing || submitting
          }
          onClick={() => {
            onRegion(selection);
            setSelect(false);
          }}
        >
          以原页坐标重识别此区域
        </button>
      )}
      <p className="ebook-muted">漏题时可拖框补充识别，已校对题目会保留。缩放、旋转只改变预览。</p>
    </>
  );
}
type EditorStep = 'SETUP' | 'INFO' | 'ANSWER';
export function EbookEditor({
  client,
  document,
  question: q,
  onChange,
  onPersist,
  onComplete,
  onCompleteAndNext,
  onBusyChange,
  hasNextQuestion = false,
  dirty = false,
  onRegion,
  busy,
  chapters,
  onCreateChapter,
  onRenameChapter,
  chapterQuestionCount,
}: {
  client: EbookClient;
  document: EbookDocument;
  question: EbookQuestion;
  onChange: (q: EbookQuestion) => void;
  onPersist: (q: EbookQuestion) => Promise<void>;
  onComplete: (q: EbookQuestion) => Promise<void>;
  onCompleteAndNext?: (q: EbookQuestion) => Promise<void>;
  onBusyChange?: (busy: boolean) => void;
  hasNextQuestion?: boolean;
  dirty?: boolean;
  onRegion: (region: { x: number; y: number; width: number; height: number }) => void;
  busy: boolean;
  chapters: readonly string[];
  onCreateChapter: (name: string, question: EbookQuestion) => Promise<void>;
  onRenameChapter: (currentName: string, nextName: string) => Promise<void>;
  chapterQuestionCount?: number;
}) {
  const [step, setStep] = useState<EditorStep>('SETUP');
  const [tool, setTool] = useState('BLACK');
  const [mark, setMark] = useState('A');
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [position, setPosition] = useState({ stones: q.stones, currentPlayer: q.currentPlayer });
  const [pending, setPending] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('board');
  const [practice, setPractice] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const boardInput = useRef<HTMLDivElement>(null);
  const wheelState = useRef(initialEbookWheelState());
  const wheelHandler = useRef<(event: WheelEvent) => void>(() => {});
  const navigationLock = useRef(false);
  const pointerButton = useRef(0);
  const [trialBusy, setTrialBusy] = useState(false);
  const mounted = useRef(true);
  const activeResult = <T,>(request: Promise<T>) =>
    activeEbookEditorResult(request, () => mounted.current);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useLayoutEffect(() => {
    onBusyChange?.(pending || replaying || trialBusy);
    return () => onBusyChange?.(false);
  }, [pending, replaying, trialBusy, onBusyChange]);
  const [branchView, setBranchView] = useState<'list' | 'tree'>('list');
  const [branchPage, setBranchPage] = useState(0);
  const [branchTail, setBranchTail] = useState<string | null>(null);
  const [replayFailed, setReplayFailed] = useState(false);
  const [replayVersion, setReplayVersion] = useState(0);
  const inFlight = useRef(false);
  const [showTrails, setShowTrails] = useState(true);
  const [boardCorner, setBoardCorner] = useState<HomeworkBoardCorner | null>(null);
  const undo = useRef<EbookQuestion[]>([]);
  const redo = useRef<EbookQuestion[]>([]);
  const deletedBranches = useRef<DeletedEbookAnswerBranch[]>([]);
  const forward = useRef(new Map<string | null, string>());
  const [anchorX, setAnchorX] = useState(q.visible.x);
  const [anchorY, setAnchorY] = useState(q.visible.y);
  const locked = pending || busy || replaying;
  useEffect(() => {
    setAnchorX(q.visible.x);
    setAnchorY(q.visible.y);
    setBoardCorner(null);
  }, [q.visible.x, q.visible.y]);
  useEffect(() => {
    setTab('board');
  }, [step]);
  const pathKey = JSON.stringify(
    step === 'ANSWER' && q.nodes.some((n) => n.id === nodeId) ? pathFor(q, nodeId) : [],
  );
  useEffect(() => {
    let alive = true;
    const valid = q.nodes.some((n) => n.id === nodeId) ? nodeId : null;
    if (valid !== nodeId) setNodeId(valid);
    if (step !== 'ANSWER') {
      setPosition({ stones: q.stones, currentPlayer: q.currentPlayer });
      setReplaying(false);
      navigationLock.current = false;
      return;
    }
    setReplaying(true);
    setReplayFailed(false);
    void client
      .trial(document.id, q, pathFor(q, valid))
      .then((p) => {
        if (alive) setPosition(p);
      })
      .catch((e: unknown) => {
        if (alive) {
          setReplayFailed(true);
          setError(userErrorMessage(e, '局面加载失败，请重试后继续录入'));
        }
      })
      .finally(() => {
        if (alive) {
          navigationLock.current = false;
          setReplaying(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [
    client,
    document.id,
    q.stones,
    q.size,
    q.currentPlayer,
    pathKey,
    nodeId,
    step,
    replayVersion,
  ]);
  const update = (next: EbookQuestion) => {
    if (!mounted.current) return;
    if (ebookAnswerPositionKey(next) !== ebookAnswerPositionKey(q)) deletedBranches.current = [];
    undo.current.push(q);
    redo.current = [];
    onChange(next);
  };
  const field = <K extends keyof EbookQuestion>(key: K, value: EbookQuestion[K]) =>
    update({
      ...q,
      [key]: value,
      ...(['visible', 'outside', 'assumption', 'role'].includes(key)
        ? { faceConfirmed: false, geometryConfirmed: false }
        : {}),
      ...([
        'goal',
        'currentPlayer',
        'size',
        'visible',
        'outside',
        'assumption',
        'role',
        'closedWorld',
        'answerSource',
      ].includes(key)
        ? { answerConfirmed: false }
        : {}),
    });
  const action = async (fn: () => Promise<void>) => {
    if (!mounted.current || inFlight.current || pending || busy) return;
    inFlight.current = true;
    onBusyChange?.(true);
    setPending(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      if (mounted.current) setError(userErrorMessage(e, '操作失败'));
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  };
  const safe = (fn: () => EbookQuestion) => {
    try {
      const next = fn();
      deletedBranches.current = [];
      update(next);
      setError('');
    } catch (e) {
      setError(userErrorMessage(e, '变换失败'));
    }
  };
  const current = q.nodes.find((n) => n.id === nodeId);
  const savedQuestion = document.data.questions.find((item) => item.id === q.id);
  const boardViewport = boardCorner === null ? null : boardViewportForCorner(boardCorner);
  const nextMoves = step === 'ANSWER' ? savedNextMoves(q, savedQuestion, current?.id ?? null) : [];
  const moveLabels =
    step === 'ANSWER' && !replaying
      ? ebookMoveLabels(q.size, position.stones, pathFor(q, current?.id ?? null))
      : [];
  const children = q.nodes.filter((n) => n.parentId === nodeId);
  const inputState = (fromSettings = false) => ({
    answer: step === 'ANSWER',
    busy: locked || replayFailed || navigationLock.current || inFlight.current || !mounted.current,
    modal:
      (settingsOpen && !fromSettings) ||
      (Boolean(globalThis.document.querySelector('dialog[open]')) &&
        !(fromSettings && settingsOpen)),
  });
  const point = async (p: { x: number; y: number } | null, fromSettings = false) => {
    if (
      locked ||
      replayFailed ||
      step === 'INFO' ||
      navigationLock.current ||
      (!fromSettings && (settingsOpen || globalThis.document.querySelector('dialog[open]')))
    )
      return;
    if (step === 'SETUP') {
      if (!p) return;
      const stones = [...q.stones];
      let marks = [...q.marks];
      if (['BLACK', 'WHITE', 'ERASE'].includes(tool)) {
        stones[p.y * q.size + p.x] = tool === 'BLACK' ? 'BLACK' : tool === 'WHITE' ? 'WHITE' : null;
        if (tool === 'ERASE') marks = marks.filter((m) => m.point.x !== p.x || m.point.y !== p.y);
      } else {
        marks = marks.filter((m) => m.point.x !== p.x || m.point.y !== p.y);
        marks.push({
          point: p,
          kind: tool as 'LABEL' | 'TRIANGLE' | 'CIRCLE' | 'SQUARE' | 'CROSS',
          label: tool === 'LABEL' ? mark : '',
        });
      }
      update({
        ...q,
        stones,
        marks,
        uncertain: q.uncertain.filter((u) => u.x !== p.x || u.y !== p.y),
        faceConfirmed: false,
        answerConfirmed: false,
      });
      return;
    }
    await action(async () => {
      if (current && current.outcome !== 'CONTINUE')
        throw new Error('当前是终点；请后退添加其他分支，或先将此节点改为“继续”。');
      const move: EbookMove = { color: position.currentPlayer, point: p };
      const next = await activeResult(client.trial(document.id, q, [...pathFor(q, nodeId), move]));
      const existing = children.find((n) => JSON.stringify(n.move) === JSON.stringify(move));
      if (existing) {
        setNodeId(existing.id);
        return;
      }
      const id = secureRandomUuid();
      update({
        ...q,
        answerConfirmed: false,
        nodes: [...q.nodes, { id, parentId: nodeId, move, outcome: 'CONTINUE', comment: '' }],
      });
      forward.current.set(nodeId, id);
      setNodeId(id);
      setPosition(next);
    });
  };
  const confirmAnswer = async () => {
    const next = {
      ...q,
      answerConfirmed: q.mode === 'AUTO',
      closedWorld: q.mode === 'AUTO' ? true : q.closedWorld,
      answerSource:
        q.mode === 'AUTO'
          ? q.answerSource.trim() || '教师手工编辑并核对；覆盖本题已录入的变化分支'
          : q.answerSource,
    };
    await activeResult(client.practicePreview(document.id, next, []));
    update(next);
    return next;
  };
  const branchEnds = savedBranchEnds(q);
  const pageSize = 4;
  const pageCount = Math.max(1, Math.ceil(branchEnds.length / pageSize));
  const activePage = Math.min(branchPage, pageCount - 1);
  const activeBranch =
    nodeId === null
      ? undefined
      : (branchEnds.find((node) => node.id === nodeId) ??
        branchEnds.find(
          (node) =>
            node.id === branchTail && branchRecord(q, node.id)?.some((item) => item.id === nodeId),
        ) ??
        branchEnds.find((node) => branchRecord(q, node.id)?.some((item) => item.id === nodeId)));
  useEffect(() => {
    if (activeBranch)
      setBranchPage(
        Math.floor(branchEnds.findIndex((node) => node.id === activeBranch.id) / pageSize),
      );
  }, [activeBranch?.id]);
  const selectNode = (id: string | null) => {
    if (!mounted.current) return;
    setError('');
    if (id !== null) {
      for (const node of branchRecord(q, id) ?? []) forward.current.set(node.parentId, node.id);
      if (!q.nodes.some((node) => node.parentId === id)) setBranchTail(id);
    }
    if (step === 'ANSWER' && id !== nodeId) navigationLock.current = true;
    setNodeId(id);
  };
  const navigate = (direction: 'FIRST' | 'PREVIOUS' | 'NEXT' | 'LAST') => {
    const next = navigateEbookAnswer(q, nodeId, forward.current, direction, inputState());
    if (next === undefined) return;
    if (direction === 'PREVIOUS' && current) forward.current.set(current.parentId, current.id);
    selectNode(next);
  };
  const deleteCurrentBranch = (fromSettings = false) => {
    const change = deleteEbookAnswerBranch(q, nodeId, inputState(fromSettings));
    if (!change) return;
    deletedBranches.current.push(change.deleted);
    onChange(change.question);
    selectNode(change.selected);
  };
  const restoreDeletedBranch = () => {
    const deleted = deletedBranches.current.at(-1);
    if (!deleted) return;
    try {
      const restored = restoreEbookAnswerBranch(q, deleted, inputState(true));
      if (!restored) return;
      deletedBranches.current.pop();
      onChange(restored);
      forward.current.set(deleted.parentId, deleted.selectedId);
      selectNode(deleted.selectedId);
    } catch (e) {
      setError(userErrorMessage(e, '恢复删除失败'));
    }
  };
  useLayoutEffect(() => {
    wheelHandler.current = (event) => {
      const next = ebookWheelNavigation(
        wheelState.current,
        event,
        performance.now(),
        ebookEditorInputAllowed(inputState()),
      );
      wheelState.current = next.state;
      if (next.handled) event.preventDefault();
      if (next.direction) navigate(next.direction);
    };
  });
  useEffect(() => {
    const element = boardInput.current;
    if (!element || step !== 'ANSWER' || practice) return;
    const listener = (event: WheelEvent) => wheelHandler.current(event);
    element.addEventListener('wheel', listener, { passive: false });
    return () => element.removeEventListener('wheel', listener);
  }, [step, practice]);
  const outcome = (value: EbookQuestion['nodes'][number]['outcome']) => {
    if (!current || locked) return;
    if (value !== 'CONTINUE' && children.length) {
      setError('此处还有后续变化，请到末手标记结果。');
      return;
    }
    setError('');
    update({
      ...q,
      answerConfirmed: false,
      nodes: q.nodes.map((n) => (n.id === current.id ? { ...n, outcome: value } : n)),
    });
  };
  const enterAnswer = () =>
    void action(async () => {
      const next = {
        ...q,
        closedWorld: q.mode === 'AUTO' ? true : q.closedWorld,
        title: q.title === '待校对题目' ? `第 ${q.number} 题` : q.title,
      };
      await activeResult(client.trial(document.id, next, []));
      await activeResult(onPersist(next));
      setStep('ANSWER');
      setTab('board');
    });
  const preview = () =>
    void action(async () => {
      const next = await confirmAnswer();
      await activeResult(onPersist(next));
      setPractice(true);
    });
  const confirmPosition = () =>
    void action(async () => {
      const modelProblem = outsideModelProblem(q);
      if (modelProblem) throw new Error(modelProblem);
      const next = { ...q, role: 'PROBLEM' as const, geometryConfirmed: true, faceConfirmed: true };
      await activeResult(client.trial(document.id, next, []));
      update(next);
      setStep('INFO');
      setTab('properties');
    });
  const chapterSelect = (
    <EbookChapterSelect
      chapter={normalizeEbookChapter(q.chapter)}
      chapters={chapters}
      disabled={locked}
      onChange={(chapter) => field('chapter', chapter)}
      onCreate={(name) => onCreateChapter(name, q)}
      onRename={onRenameChapter}
      questionCount={
        chapterQuestionCount ??
        document.data.questions.filter(
          (item) =>
            normalizeEbookChapter(item.id === q.id ? q.chapter : item.chapter) ===
            normalizeEbookChapter(q.chapter),
        ).length
      }
    />
  );
  if (practice)
    return (
      <EbookEditorTrial
        client={client}
        documentId={document.id}
        question={q}
        onBack={() => setPractice(false)}
        onBusyChange={setTrialBusy}
      />
    );
  return (
    <div className={`ebook-editor-workspace is-${step.toLowerCase()}`} aria-busy={locked}>
      <div className="ebook-editor-stepbar">
        <span className="ebook-editor-step-status">
          {q.faceConfirmed ? '棋形已确认' : '确认棋形后继续'}
          {q.uncertain.length > 0 ? ` · ${q.uncertain.length} 个识别疑点` : ''}
        </span>
        <nav className="ebook-editor-steps" aria-label="题目编辑步骤">
          {(
            [
              ['SETUP', '棋形'],
              ['INFO', '题目信息'],
              ['ANSWER', '答案变化'],
            ] as const
          ).map(([id, label], index) => (
            <button
              key={id}
              aria-current={step === id ? 'step' : undefined}
              disabled={
                locked ||
                (id !== 'SETUP' && !q.faceConfirmed) ||
                (id === 'ANSWER' && (!q.number.trim() || !q.goal.trim()))
              }
              onClick={() => (id === 'ANSWER' ? enterAnswer() : setStep(id))}
            >
              <span>{index + 1}</span>
              {label}
            </button>
          ))}
        </nav>
        <label className="ebook-editor-step-select">
          编辑步骤
          <select
            value={step}
            disabled={locked}
            onChange={(event) =>
              event.target.value === 'ANSWER'
                ? enterAnswer()
                : setStep(event.target.value as EditorStep)
            }
          >
            <option value="SETUP">1 确认棋形</option>
            <option value="INFO" disabled={!q.faceConfirmed}>
              2 题目信息
            </option>
            <option
              value="ANSWER"
              disabled={!q.faceConfirmed || !q.number.trim() || !q.goal.trim()}
            >
              3 答案变化
            </option>
          </select>
        </label>
      </div>
      {error && (
        <div className="ebook-editor-error" role="alert">
          <span>{error}</span>
          {replayFailed && (
            <button disabled={locked} onClick={() => setReplayVersion((value) => value + 1)}>
              重试局面
            </button>
          )}
          <button aria-label="关闭错误提示" onClick={() => setError('')}>
            关闭
          </button>
        </div>
      )}
      <div className="ebook-editor-pane-switch" aria-label="工作区域">
        {(step === 'ANSWER'
          ? [
              ['variations', '答案变化'],
              ['board', '棋盘'],
              ['properties', '当前分支'],
            ]
          : step === 'SETUP'
            ? [
                ['source', '原图'],
                ['board', '棋盘'],
                ['properties', '棋形确认'],
              ]
            : [
                ['board', '棋盘'],
                ['properties', '题目信息'],
              ]
        ).map(([id, label]) => (
          <button key={id} aria-pressed={tab === id} onClick={() => setTab(id!)}>
            {label}
          </button>
        ))}
      </div>
      <div
        className={`ebook-editor-panes ebook-workbench${step === 'ANSWER' ? ' ebook-workbench--answer' : ''}`}
      >
        {step === 'ANSWER' && (
          <section
            className={`ebook-editor-pane ebook-editor-branches ${tab === 'variations' ? 'is-visible' : ''}`}
            aria-label="答案变化"
          >
            <h3>
              答案变化 <span>{branchEnds.length} 条</span>
            </h3>
            <div className="ebook-editor-branch-tabs" role="group" aria-label="变化展示方式">
              <button aria-pressed={branchView === 'list'} onClick={() => setBranchView('list')}>
                分支列表
              </button>
              <button aria-pressed={branchView === 'tree'} onClick={() => setBranchView('tree')}>
                变化树
              </button>
            </div>
            {branchView === 'tree' ? (
              <EbookVariationTree
                nodes={q.nodes}
                selected={nodeId}
                disabled={locked}
                onSelect={selectNode}
              />
            ) : (
              <>
                <button
                  className="ebook-editor-root"
                  disabled={locked}
                  aria-pressed={nodeId === null}
                  onClick={() => selectNode(null)}
                >
                  初始局面
                </button>
                <div className="ebook-editor-branch-list">
                  {branchEnds
                    .slice(activePage * pageSize, activePage * pageSize + pageSize)
                    .map((node, index) => {
                      const record = branchRecord(q, node.id);
                      const active = activeBranch?.id === node.id;
                      return (
                        <button
                          key={node.id}
                          disabled={locked}
                          aria-pressed={Boolean(active)}
                          onClick={() => selectNode(node.id)}
                        >
                          <span>
                            变化 {activePage * pageSize + index + 1}
                            <b data-outcome={node.outcome}>
                              {node.outcome === 'CORRECT'
                                ? '正确'
                                : node.outcome === 'INCORRECT'
                                  ? '错误'
                                  : '录入中'}
                            </b>
                          </span>
                          <small>
                            {record?.length ?? 0} 手 ·{' '}
                            {branchIsSaved(q, savedQuestion, node.id) ? '已保存' : '草稿'}
                          </small>
                        </button>
                      );
                    })}
                  {!branchEnds.length && (
                    <p className="ebook-muted">在棋盘落子开始录入。回到分叉点，可添加其他变化。</p>
                  )}
                </div>
                <div className="ebook-editor-pagination" aria-label="分支列表翻页">
                  <button
                    aria-label="上一页分支"
                    disabled={activePage === 0}
                    onClick={() => setBranchPage(activePage - 1)}
                  >
                    <ChevronLeftIcon />
                  </button>
                  <span>
                    {activePage + 1} / {pageCount}
                  </span>
                  <button
                    aria-label="下一页分支"
                    disabled={activePage + 1 >= pageCount}
                    onClick={() => setBranchPage(activePage + 1)}
                  >
                    <ChevronRightIcon />
                  </button>
                </div>
              </>
            )}
            <label className="ebook-editor-hints">
              <input
                type="checkbox"
                checked={showTrails}
                onChange={(event) => setShowTrails(event.target.checked)}
              />
              显示已保存的下一手提示
            </label>
          </section>
        )}
        {step === 'SETUP' && (
          <section
            className={`ebook-editor-pane ebook-editor-source ${tab === 'source' ? 'is-visible' : ''}`}
          >
            <EbookSource
              client={client}
              document={document}
              pageIndex={q.source?.pageIndex ?? 0}
              question={q}
              onRegion={onRegion}
            />
            <details>
              <summary>识别依据与来源</summary>
              <p>
                {q.source?.recognitionVersion} · 印刷页 {q.source?.printedPage || '未填写'} ·{' '}
                {q.notes}
              </p>
            </details>
          </section>
        )}
        <section
          className={`ebook-editor-pane ebook-editor-board-pane ${tab === 'board' ? 'is-visible' : ''}`}
          aria-label="题目棋盘"
        >
          <div className="ebook-editor-board-tools">
            {step === 'ANSWER' ? (
              <>
                <button
                  title="回到初始局面"
                  disabled={locked || !current}
                  onClick={() => navigate('FIRST')}
                >
                  <DoubleArrowLeftIcon />
                  <span>初始</span>
                </button>
                <button
                  title="删除当前一手及后续变化（棋盘右键）；可在答案工具中恢复"
                  disabled={locked || !current}
                  onClick={() => deleteCurrentBranch()}
                >
                  撤销
                </button>
                <button
                  title="上一手（仅浏览）"
                  disabled={locked || !current}
                  onClick={() => navigate('PREVIOUS')}
                >
                  <ChevronLeftIcon />
                  <span>上一手</span>
                </button>
                <strong>第 {pathFor(q, nodeId).length} 手</strong>
                <button
                  title="下一手（仅浏览）"
                  disabled={locked || !children.length}
                  onClick={() => navigate('NEXT')}
                >
                  <span>下一手</span>
                  <ChevronRightIcon />
                </button>
                <button
                  title="浏览至当前变化末手"
                  disabled={locked || !children.length}
                  onClick={() => navigate('LAST')}
                >
                  <span>末手</span>
                  <DoubleArrowRightIcon />
                </button>
                <button
                  type="button"
                  disabled={locked}
                  aria-haspopup="dialog"
                  onClick={() => setSettingsOpen(true)}
                >
                  答案工具
                </button>
              </>
            ) : step === 'SETUP' ? (
              <>
                {(
                  [
                    ['BLACK', '黑子'],
                    ['WHITE', '白子'],
                    ['ERASE', '擦除'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    aria-pressed={tool === id}
                    disabled={locked}
                    onClick={() => setTool(id)}
                  >
                    {label}
                  </button>
                ))}
                <select
                  aria-label="棋盘标记工具"
                  value={['BLACK', 'WHITE', 'ERASE'].includes(tool) ? '' : tool}
                  disabled={locked}
                  onChange={(e) => setTool(e.target.value)}
                >
                  <option value="" disabled>
                    标记
                  </option>
                  <option value="LABEL">文字</option>
                  <option value="TRIANGLE">三角</option>
                  <option value="CIRCLE">圆形</option>
                  <option value="SQUARE">方形</option>
                  <option value="CROSS">叉号</option>
                </select>
                {tool === 'LABEL' && (
                  <input
                    className="ebook-editor-mark"
                    aria-label="标记文字"
                    value={mark}
                    maxLength={12}
                    onChange={(e) => setMark(e.target.value)}
                  />
                )}
              </>
            ) : (
              <h3>已确认棋形</h3>
            )}
            <ClassroomZoomControl
              zoomed={boardCorner !== null}
              onRestore={() => setBoardCorner(null)}
              onSelect={setBoardCorner}
            />
          </div>
          <div
            ref={boardInput}
            className="ebook-editor-board-input"
            title={
              step === 'ANSWER'
                ? '滚轮向上浏览上一手，向下浏览下一手；右键撤销当前一手及后续变化'
                : undefined
            }
            onPointerDownCapture={(event) => {
              pointerButton.current = event.button;
              if (step === 'ANSWER' && (event.button === 2 || event.ctrlKey))
                event.preventDefault();
            }}
            onClickCapture={(event) => {
              if (
                step === 'ANSWER' &&
                (event.button !== 0 ||
                  event.ctrlKey ||
                  (event.detail > 0 && pointerButton.current !== 0))
              ) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onContextMenu={(event) => {
              if (step !== 'ANSWER') return;
              event.preventDefault();
              event.stopPropagation();
              deleteCurrentBranch();
            }}
          >
            <EbookEditorBoardFrame>
              <BoardSurface
                size={q.size}
                stones={step === 'ANSWER' ? position.stones : q.stones}
                boardAppearance="wood"
                stoneAppearance="shell"
                viewport={boardViewport}
                lastMove={null}
                stoneLabels={moveLabels}
                svgOverlay={
                  step === 'ANSWER' && showTrails && !replaying ? (
                    <EbookBranchTrails size={q.size} stones={position.stones} moves={nextMoves} />
                  ) : null
                }
                initialAnnotations={q.marks
                  .filter(
                    (m) =>
                      !moveLabels.some((l) => l.point.x === m.point.x && l.point.y === m.point.y),
                  )
                  .map((m) =>
                    m.kind === 'LABEL'
                      ? { point: m.point, kind: 'LABEL' as const, text: m.label }
                      : { point: m.point, kind: m.kind },
                  )}
                pointMarkers={
                  step === 'SETUP' ? q.uncertain.map((p) => ({ point: p, color: '#d8392f' })) : []
                }
                keyboardNavigation
                isActionable={() =>
                  !locked &&
                  !settingsOpen &&
                  !navigationLock.current &&
                  !replayFailed &&
                  step !== 'INFO' &&
                  !(step === 'ANSWER' && current && current.outcome !== 'CONTINUE')
                }
                onPoint={(p) => void point(p)}
              />
            </EbookEditorBoardFrame>
          </div>
          {step === 'SETUP' && (
            <div className="ebook-editor-board-foot">
              <span>自由摆子 · 不提子、不交替</span>
              <button
                disabled={locked || !undo.current.length}
                onClick={() => {
                  const old = undo.current.pop();
                  if (old) {
                    redo.current.push(q);
                    onChange(old);
                  }
                }}
              >
                撤销
              </button>
              <button
                disabled={locked || !redo.current.length}
                onClick={() => {
                  const next = redo.current.pop();
                  if (next) {
                    undo.current.push(q);
                    onChange(next);
                  }
                }}
              >
                重做
              </button>
            </div>
          )}
        </section>
        <section
          className={`ebook-editor-pane ebook-editor-properties ${tab === 'properties' ? 'is-visible' : ''}`}
        >
          <h3>
            {step === 'SETUP'
              ? '棋形确认'
              : step === 'INFO'
                ? '告诉学生这道题要做什么'
                : '当前分支'}
          </h3>
          <fieldset className="ebook-editor-fields" disabled={locked}>
            {step === 'SETUP' && chapterSelect}
            {step === 'SETUP' && (
              <>
                <label>
                  真实棋盘
                  <select
                    value={q.size}
                    onChange={(e) =>
                      safe(() => resizeQuestion(q, Number(e.target.value) as EbookQuestion['size']))
                    }
                  >
                    {[9, 13, 19].map((size) => (
                      <option key={size} value={size}>
                        {size} 路
                      </option>
                    ))}
                  </select>
                </label>
                <details>
                  <summary>局部位置与方向</summary>
                  <p className="ebook-muted">
                    识别范围 {q.visible.columns} × {q.visible.rows} · 原点 ({q.visible.x},{' '}
                    {q.visible.y})
                  </p>
                  <div className="ebook-actions">
                    {(['左上', '右上', '左下', '右下'] as const).map((corner) => (
                      <button
                        key={corner}
                        onClick={() =>
                          safe(() =>
                            anchorQuestion(
                              q,
                              corner.includes('右') ? q.size - q.visible.columns : 0,
                              corner.includes('下') ? q.size - q.visible.rows : 0,
                              q.size,
                            ),
                          )
                        }
                      >
                        {corner}角
                      </button>
                    ))}
                    <button onClick={() => safe(() => transformQuestion(q, 'ROTATE'))}>
                      旋转 90°
                    </button>
                    <button onClick={() => safe(() => transformQuestion(q, 'MIRROR'))}>镜像</button>
                  </div>
                  <div className="ebook-compact">
                    <label>
                      原点 X
                      <input
                        type="number"
                        min={0}
                        max={18}
                        value={anchorX}
                        onChange={(e) => setAnchorX(Number(e.target.value))}
                      />
                    </label>
                    <label>
                      原点 Y
                      <input
                        type="number"
                        min={0}
                        max={18}
                        value={anchorY}
                        onChange={(e) => setAnchorY(Number(e.target.value))}
                      />
                    </label>
                    <label>
                      可见列
                      <input
                        type="number"
                        min={2}
                        max={q.size}
                        value={q.visible.columns}
                        onChange={(e) =>
                          field('visible', { ...q.visible, columns: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label>
                      可见行
                      <input
                        type="number"
                        min={2}
                        max={q.size}
                        value={q.visible.rows}
                        onChange={(e) =>
                          field('visible', { ...q.visible, rows: Number(e.target.value) })
                        }
                      />
                    </label>
                  </div>
                  <button onClick={() => safe(() => anchorQuestion(q, anchorX, anchorY, q.size))}>
                    应用位置
                  </button>
                </details>
                <label>
                  图外区域
                  <select
                    aria-label="未显示区域"
                    value={
                      q.outside === 'COMPLETE' && !sourceShowsFullBoard(q) ? 'UNKNOWN' : q.outside
                    }
                    onChange={(e) => {
                      setError('');
                      update(chooseOutsideModel(q, e.target.value as EbookQuestion['outside']));
                    }}
                  >
                    <option value="UNKNOWN">尚未确认</option>
                    <option value="CONFIRMED_EMPTY">按图外空盘练习</option>
                    <option value="COMPLETE" disabled={!sourceShowsFullBoard(q)}>
                      原图是完整棋盘
                    </option>
                  </select>
                </label>
                {!sourceShowsFullBoard(q) && (
                  <p className="ebook-muted">原图未显示的局面，需要教师确认后才能练习。</p>
                )}
                <div
                  className={`ebook-editor-position-status ${q.uncertain.length ? 'has-risk' : ''}`}
                >
                  {q.uncertain.length
                    ? `请在棋盘校正 ${q.uncertain.length} 个红色疑点`
                    : '已校正全部识别疑点'}
                </div>
                {q.outside === 'CONFIRMED_EMPTY' && (
                  <details>
                    <summary>补充说明</summary>
                    <label>
                      建模假设
                      <textarea
                        aria-label="建模假设"
                        value={q.assumption}
                        onChange={(e) => field('assumption', e.target.value)}
                      />
                    </label>
                  </details>
                )}
              </>
            )}
            {step === 'INFO' && (
              <>
                <div className="ebook-editor-info-grid">
                  <label>
                    <span>
                      题号 <em>*</em>
                    </span>
                    <input
                      value={q.number}
                      maxLength={40}
                      onChange={(e) => field('number', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>
                      先行方 <em>*</em>
                    </span>
                    <select
                      value={q.currentPlayer}
                      onChange={(e) =>
                        field('currentPlayer', e.target.value as EbookQuestion['currentPlayer'])
                      }
                    >
                      <option value="BLACK">黑先</option>
                      <option value="WHITE">白先</option>
                    </select>
                  </label>
                </div>
                <label>
                  <span>
                    题目要求 <em>*</em>
                  </span>
                  <textarea
                    placeholder="例如：黑先，找到占据要点的一手。"
                    value={q.goal}
                    onChange={(e) => field('goal', e.target.value)}
                  />
                </label>
                {chapterSelect}
                <details>
                  <summary>补充分类、难度与讲解（可选）</summary>
                  <label>
                    标题
                    <input value={q.title} onChange={(e) => field('title', e.target.value)} />
                  </label>
                  <label>
                    题干
                    <textarea value={q.text} onChange={(e) => field('text', e.target.value)} />
                  </label>
                  {(['category', 'difficulty', 'hint', 'explanation', 'notes'] as const).map(
                    (key, i) => (
                      <label key={key}>
                        {['分类', '难度（未知留空）', '提示', '讲解', '备注'][i]}
                        <textarea value={q[key]} onChange={(e) => field(key, e.target.value)} />
                      </label>
                    ),
                  )}
                  <label>
                    印刷页码
                    <input
                      value={q.source?.printedPage ?? ''}
                      onChange={(e) => {
                        if (q.source) field('source', { ...q.source, printedPage: e.target.value });
                      }}
                    />
                  </label>
                </details>
              </>
            )}
            {step === 'ANSWER' && (
              <>
                <p className="ebook-editor-current-ply">
                  {nodeId ? `第 ${pathFor(q, nodeId).length} 手` : '初始局面'}
                </p>
                <div className="ebook-editor-turn">
                  {current && current.outcome !== 'CONTINUE' ? (
                    <>
                      <strong>本变化已结束</strong>
                      <button onClick={() => outcome('CONTINUE')}>继续录入</button>
                    </>
                  ) : (
                    <strong>
                      {replaying
                        ? '正在加载局面…'
                        : replayFailed
                          ? '局面加载失败'
                          : `下一手：${position.currentPlayer === 'BLACK' ? '黑棋' : '白棋'}`}
                    </strong>
                  )}
                </div>
                <fieldset className="ebook-editor-outcomes" disabled={locked || !current}>
                  <legend>此处结果</legend>
                  {(
                    [
                      ['CONTINUE', '继续作答'],
                      ['CORRECT', '正确结束'],
                      ['INCORRECT', '错误结束'],
                    ] as const
                  ).map(([value, label]) => (
                    <label
                      key={value}
                      data-outcome={value}
                      className={current?.outcome === value ? 'is-selected' : ''}
                    >
                      <input
                        type="radio"
                        name={`outcome-${q.id}`}
                        value={value}
                        checked={current?.outcome === value}
                        onChange={() => outcome(value)}
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>
                <label className="ebook-editor-comment">
                  变化讲解（可选）
                  <textarea
                    disabled={!current}
                    value={current?.comment ?? ''}
                    placeholder={current ? '填写这一变化的讲解' : '先在棋盘落子，再添加讲解'}
                    onChange={(e) => {
                      if (current)
                        update({
                          ...q,
                          nodes: q.nodes.map((n) =>
                            n.id === current.id ? { ...n, comment: e.target.value } : n,
                          ),
                        });
                    }}
                  />
                </label>
              </>
            )}
          </fieldset>
          {step === 'ANSWER' && (
            <div className="ebook-editor-grading-panel">
              <fieldset className="ebook-editor-outcomes ebook-editor-grading" disabled={locked}>
                <legend>批改方式</legend>
                {(
                  [
                    ['AUTO', '自动批改'],
                    ['MANUAL', '手动批改'],
                  ] as const
                ).map(([mode, label]) => (
                  <label key={mode} className={q.mode === mode ? 'is-selected' : ''}>
                    <input
                      type="radio"
                      name={`grading-${q.id}`}
                      value={mode}
                      checked={q.mode === mode}
                      onChange={() =>
                        update({ ...q, mode, closedWorld: true, answerConfirmed: false })
                      }
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
              <p className="ebook-editor-grading-note">
                {q.mode === 'AUTO' ? '自动批改：未录入的着法判错。' : '本题由教师批改。'}
              </p>
            </div>
          )}
          {step === 'ANSWER' && (
            <div className="ebook-editor-answer-actions">
              <button disabled={locked} onClick={preview}>
                试做
              </button>
              <button
                className="primary"
                disabled={locked}
                onClick={() =>
                  void action(async () => {
                    const next = await confirmAnswer();
                    await activeResult(onComplete(next));
                  })
                }
              >
                保存分支
              </button>
              <button
                disabled={locked || !hasNextQuestion || !onCompleteAndNext}
                onClick={() =>
                  void action(async () => {
                    if (!onCompleteAndNext) return;
                    const next = await confirmAnswer();
                    await activeResult(onCompleteAndNext(next));
                  })
                }
              >
                保存并下一题 <ChevronRightIcon />
              </button>
            </div>
          )}
        </section>
      </div>
      {step !== 'ANSWER' && (
        <footer className="ebook-editor-footer">
          <div className="ebook-editor-footer-status" role="status">
            <span className={dirty ? 'is-dirty' : ''}>
              {dirty ? '有未保存修改' : '当前修改已保存'}
            </span>
          </div>
          <div className="ebook-editor-footer-actions">
            {step === 'SETUP' ? (
              <>
                <button disabled={locked} onClick={() => void action(() => onPersist(q))}>
                  暂存草稿
                </button>
                <button
                  className="primary"
                  disabled={locked || Boolean(q.uncertain.length)}
                  onClick={confirmPosition}
                >
                  确认棋形 <ChevronRightIcon />
                </button>
              </>
            ) : step === 'INFO' ? (
              <>
                <button disabled={locked} onClick={() => setStep('SETUP')}>
                  上一步
                </button>
                <button
                  className="primary"
                  disabled={locked || !q.number.trim() || !q.goal.trim()}
                  onClick={enterAnswer}
                >
                  录入答案 <ChevronRightIcon />
                </button>
              </>
            ) : null}
          </div>
        </footer>
      )}
      {settingsOpen && (
        <EbookEditorSettingsDialog onClose={() => setSettingsOpen(false)}>
          {error && (
            <p role="alert" className="ebook-error">
              {error}
            </p>
          )}
          <fieldset className="ebook-editor-fields" disabled={locked}>
            <div className="ebook-actions">
              <button disabled={!current} onClick={() => deleteCurrentBranch(true)}>
                删除当前分支
              </button>
              <button disabled={!deletedBranches.current.length} onClick={restoreDeletedBranch}>
                恢复删除
              </button>
              <button
                disabled={replayFailed || Boolean(current && current.outcome !== 'CONTINUE')}
                onClick={() => void point(null, true)}
              >
                虚着
              </button>
            </div>
            <p className="ebook-muted">上一手只浏览；删除会移除当前节点及后续，可恢复删除。</p>
            <label>
              答案来源与覆盖范围
              <textarea
                value={q.answerSource}
                placeholder="未填写时记录为教师手工编辑并核对"
                onChange={(e) => field('answerSource', e.target.value)}
              />
            </label>
            <label>
              导入 SGF 答案
              <input
                type="file"
                accept=".sgf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file)
                    void action(async () => {
                      if (file.size > 1000000) throw new Error('SGF 大小超限');
                      const sgf = await activeResult(file.text());
                      const imported = await activeResult(client.importSgf(document.id, q, sgf));
                      deletedBranches.current = [];
                      update(imported);
                      selectNode(null);
                    });
                }}
              />
            </label>
          </fieldset>
        </EbookEditorSettingsDialog>
      )}
    </div>
  );
}
